import { Droppable } from "@hello-pangea/dnd";
import { withErrorBoundary } from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import Error from "@components/error/error";
import MotionDiv from "@components/motion/motion-div";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import { MeetingWebsocketProviderContext } from "../context";
import useMeetingWebsocketProvider from "../use-meeting-websocket-provider";
import AddTopicHover from "./add-topic-hover";
import DragAndDrop from "./drag-and-drop";
import Topic from "./topic";

const Meeting = ({
  meeting,
  isInExtension = false, // true when this view appears in an extension
  opensTopicsInSidebar,
  currentPageMeeting = null,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  isInExtension?: boolean; // true when this view appears in an extension
  opensTopicsInSidebar: boolean;
  currentPageMeeting?: MeetingViewMeetingNodeNewPageFragmentFragment | null;
}) => {
  const createTopicFormRef = useRef<HTMLDivElement | null>(null);
  const meetingsGroupedById = {} as {
    [key: number]: MeetingViewMeetingNodeNewPageFragmentFragment;
  };
  meetingsGroupedById[meeting.id] = meeting;
  const topics = assertEdgesNonNull(meeting.topics);
  const [discussedTopicIds, setDiscussedTopicIds] = useState<Set<number>>(
    new Set(),
  );

  const handleToggleDiscussed = useCallback((topicId: number) => {
    setDiscussedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  }, []);

  const sortedTopics = [...topics].sort((a, b) => {
    const aDiscussed = discussedTopicIds.has(a.id);
    const bDiscussed = discussedTopicIds.has(b.id);
    if (aDiscussed === bDiscussed) return 0;
    return aDiscussed ? 1 : -1;
  });

  const meetingWebsocketContext = useMeetingWebsocketProvider(meeting);

  useEffect(() => {
    return function cleanup() {
      const websocketConfigs = Object.values(meetingWebsocketContext);
      websocketConfigs.forEach(({ providerWebsocket, ydoc }) => {
        providerWebsocket?.destroy();
        ydoc?.destroy();
      });
    };
  }, []);

  if (!meeting.meetingGroup) {
    return <Error title="This meeting does not exist." />;
  }

  return (
    <DragAndDrop meetingsGroupedById={meetingsGroupedById}>
      <div aria-label={`Meeting: ${meeting.title}`}>
        <div>
          <Droppable droppableId={`meeting-${meeting.id}`}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {sortedTopics.length > 0 && (
                  <ul className="fs-mask" aria-label="Topic list">
                    <MeetingWebsocketProviderContext.Provider
                      value={meetingWebsocketContext}
                    >
                      {sortedTopics.map((topic, index) => (
                        <MotionDiv
                          key={topic.id}
                          layout="position"
                          transition={{
                            layout: { duration: 0.15, ease: "easeInOut" },
                          }}
                          className={classNames(isInExtension && "px-4")}
                        >
                          {index === 0 && (
                            <AddTopicHover
                              meeting={meeting}
                              beforeTopicId={topic.id}
                            />
                          )}
                          <Topic
                            index={index}
                            topic={topic}
                            meeting={meeting}
                            isInExtension={isInExtension}
                            opensInSidebar={opensTopicsInSidebar}
                            currentPageMeeting={currentPageMeeting}
                            isDiscussed={discussedTopicIds.has(topic.id)}
                            onToggleDiscussed={() =>
                              handleToggleDiscussed(topic.id)
                            }
                            autoFocus={index === 0}
                          />
                          {index >= 0 && (
                            <AddTopicHover
                              meeting={meeting}
                              afterTopicId={topic.id}
                            />
                          )}
                        </MotionDiv>
                      ))}
                    </MeetingWebsocketProviderContext.Provider>
                    {provided.placeholder}
                  </ul>
                )}
                <div ref={createTopicFormRef} />
              </div>
            )}
          </Droppable>
        </div>
      </div>
    </DragAndDrop>
  );
};

const MeetingWithSentry = withErrorBoundary(Meeting, {
  fallback: <Error description={"The meeting could not be rendered."} />,
});

export default MeetingWithSentry;
